import {last} from 'lodash';

import {FeatureFlagsMap} from '../../../store/featureFlags/featureFlagsState';
import {RuleTriggersTypesEnum} from '../../rules/ruleTriggerModel';
import {getCustomFieldFromResultType, hasTransformerFeatureFlag} from './expressionAtomHelpers';
import {ClientAtom, PreAtom} from './expressionAtomModel';
import {useExpressionsFeatureFlags} from './expressionFeatureFlags';
import {buildPropertyPreAtom} from './expressionInferHelpers';
import {AtomDescription, getMethodLabel, getPropertyDescription} from './expressionLabels';
import {PreExpressionItem} from './expressionModel';
import {ExpressionsContext, useExpressionsContext} from './expressionsContext';
import {ExpressionDependencies} from './expressionState';
import {ResultTypesEnum} from './infer/expressionResultModel';
import {buildAtom, buildAtoms, getResultType} from './infer/helpers';
import {activityTriggerProperties, baseFactoryMethods, MethodCategoriesEnum} from './infer/internal';
import {isTransformerId, TransformerIdsEnum} from './infer/transformers';

export enum ExpressionsGroupsEnum {
  CONVERSATION = 'conversation',
  CONTACT = 'contact',
  MESSAGE = 'message',
  COMMENT = 'comment',
  FACTORY = 'factory',
  ENVIRONMENT = 'environment',
  TRIGGERS = 'triggers',
  NETWORK = 'network',
  TABLE = 'table',
}

interface ExpressionsGroup {
  id: ExpressionsGroupsEnum;
  items: ReadonlyArray<PreExpressionItem>;
}

export function useExpressionsGroups(
  dependencies: ExpressionDependencies,
  triggers?: ReadonlyArray<RuleTriggersTypesEnum>,
): ReadonlyArray<ExpressionsGroup> {
  const context = useExpressionsContext();
  const featureFlags = useExpressionsFeatureFlags();

  switch (context.initialInfer.type) {
    case ResultTypesEnum.ACTIVITY:
      return buildActivityExpressionsGroups(context, featureFlags, dependencies, triggers);

    case ResultTypesEnum.AUTOLINKER:
      return buildAutolinkerExpressionsGroups(context, featureFlags, dependencies);

    case ResultTypesEnum.PREVIEWER:
      return buildPreviewerExpressionsGroups(context, featureFlags, dependencies);

    default:
      return [];
  }
}

export function buildActivityExpressionsGroups(
  context: ExpressionsContext,
  featureFlags: FeatureFlagsMap,
  dependencies: ExpressionDependencies,
  triggers?: ReadonlyArray<RuleTriggersTypesEnum>,
): ReadonlyArray<ExpressionsGroup> {
  return [
    {
      id: ExpressionsGroupsEnum.TABLE,
      items: buildTableColumns(context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.CONVERSATION,
      items: buildPropertiesAtPath(['conversation'], context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.MESSAGE,
      items: [
        ...buildPropertiesAtPath(['message'], context, dependencies),
        buildMethodAtPath(
          ['message'],
          {transform: TransformerIdsEnum.GET_MESSAGE_BODY, params: {include_quote: false}},
          context,
          dependencies,
        ),
        buildMethodAtPath(
          ['message'],
          {transform: TransformerIdsEnum.GET_RECIPIENTS, params: {roles: 'all'}},
          context,
          dependencies,
        ),
        buildMethodAtPath(['message'], {transform: TransformerIdsEnum.GET_HEADER}, context, dependencies),
        buildMethodAtPath(
          ['message'],
          {transform: TransformerIdsEnum.DETECT_LANGUAGE},
          context,
          dependencies,
        ),
      ],
    },
    {
      id: ExpressionsGroupsEnum.CONTACT,
      items: [
        ...buildPropertiesAtPath(['conversation', 'contact'], context, dependencies),
        ...buildCustomFieldsAtPath(['conversation', 'contact'], context, dependencies),
      ],
    },
    {
      id: ExpressionsGroupsEnum.COMMENT,
      items: buildPropertiesAtPath(['comment'], context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.TRIGGERS,
      items: buildTriggerProperties(triggers, context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.FACTORY,
      items: buildMethodItems(context, featureFlags, dependencies),
    },
  ].filter((category) => category.items.length > 0);
}

function buildAutolinkerExpressionsGroups(
  context: ExpressionsContext,
  featureFlags: FeatureFlagsMap,
  dependencies: ExpressionDependencies,
): ReadonlyArray<ExpressionsGroup> {
  return [
    {
      id: ExpressionsGroupsEnum.ENVIRONMENT,
      items: buildPropertiesAtPath([], context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.FACTORY,
      items: buildMethodItems(context, featureFlags, dependencies),
    },
  ];
}

function buildPreviewerExpressionsGroups(
  context: ExpressionsContext,
  featureFlags: FeatureFlagsMap,
  dependencies: ExpressionDependencies,
): ReadonlyArray<ExpressionsGroup> {
  return [
    {
      id: ExpressionsGroupsEnum.ENVIRONMENT,
      items: buildPropertiesAtPath([], context, dependencies),
    },
    {
      id: ExpressionsGroupsEnum.NETWORK,
      items: [buildFetcher(context, dependencies)],
    },
    {
      id: ExpressionsGroupsEnum.FACTORY,
      items: buildMethodItems(context, featureFlags, dependencies, MethodCategoriesEnum.CREATE),
    },
  ];
}

/*
 * Helpers.
 */

function buildPreExpressionItem(
  path: ReadonlyArray<ClientAtom>,
  atom: ClientAtom,
  description: AtomDescription,
): PreExpressionItem {
  return {
    id: description.name,
    atoms: [...path, atom],
    inferredType: atom.inferredType,
    ...description,
  };
}

function buildPropertyPath(
  path: ReadonlyArray<string>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  return buildAtoms(path.map(buildPropertyPreAtom), context.initialInfer, context, dependencies);
}

function buildPropertiesAtPath(
  path: ReadonlyArray<string>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<PreExpressionItem> {
  const atomPath = buildPropertyPath(path, context, dependencies);
  const inferredType = getLastInfer(atomPath, context);
  const resultType = getResultType(inferredType);

  return Object.keys(resultType.properties).map((property) => {
    const atom = buildAtom(buildPropertyPreAtom(property), inferredType, context, dependencies);
    const description = getPropertyDescription(property, resultType.name);

    return buildPreExpressionItem(atomPath, atom, description);
  });
}

function buildMethodAtPath(
  path: ReadonlyArray<string>,
  preAtom: PreAtom,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  const atomPath = buildPropertyPath(path, context, dependencies);
  const inferredType = getLastInfer(atomPath, context);

  const atom = buildAtom(preAtom, inferredType, context, dependencies);
  const description = getMethodLabel(preAtom.transform);

  return buildPreExpressionItem(atomPath, atom, description);
}

function buildCustomFieldsAtPath(
  path: ReadonlyArray<string>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  const atomPath = buildPropertyPath(path, context, dependencies);
  const inferredType = getLastInfer(atomPath, context);
  const resultType = getResultType(inferredType);
  const customFields = getCustomFieldFromResultType(resultType, context);

  return customFields.map((customField) => {
    const atom = buildAtom(
      {
        transform: TransformerIdsEnum.GET_CUSTOM_FIELD,
        params: {
          custom_field: customField.id,
        },
      },
      inferredType,
      context,
      dependencies,
    );

    return buildPreExpressionItem(atomPath, atom, {
      name: customField.name,
      tooltip: customField.description,
    });
  });
}

function buildTableColumns(context: ExpressionsContext, dependencies: ExpressionDependencies) {
  if (!dependencies.dataTable) {
    return [];
  }

  return dependencies.dataTable.columns.map((column) => {
    const columnAtom = buildAtom(
      {
        transform: TransformerIdsEnum.GET_COLUMN_VALUE,
        params: {
          column_id: column.id,
        },
      },
      context.initialInfer,
      context,
      dependencies,
    );

    return buildPreExpressionItem([], columnAtom, {
      name: column.name,
    });
  });
}

function getLastInfer(atoms: ReadonlyArray<ClientAtom>, context: ExpressionsContext) {
  const lastAtom = last(atoms);
  return lastAtom ? lastAtom.inferredType : context.initialInfer;
}

function buildMethodItems(
  context: ExpressionsContext,
  featureFlags: FeatureFlagsMap,
  dependencies: ExpressionDependencies,
  category?: MethodCategoriesEnum,
) {
  return Object.entries(baseFactoryMethods)
    .filter(
      ([, transformer]) =>
        hasTransformerFeatureFlag(transformer, featureFlags) &&
        (!category || category === transformer.category),
    )
    .map(([transformerId]) => transformerId)
    .filter(isTransformerId)
    .map((creatorId) => {
      const atom = buildAtom({transform: creatorId}, context.initialInfer, context, dependencies);
      const description = getMethodLabel(creatorId);

      return buildPreExpressionItem([], atom, description);
    });
}

function buildTriggerProperties(
  triggers: ReadonlyArray<RuleTriggersTypesEnum> | undefined,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
): ReadonlyArray<PreExpressionItem> {
  if (!triggers) {
    return [];
  }

  const resultType = getResultType(context.initialInfer);
  const availableTriggerProperties = triggers
    .map((trigger) => activityTriggerProperties[trigger] ?? [])
    .flat();

  return availableTriggerProperties.map((property) => {
    const atom = buildAtom(buildPropertyPreAtom(property), context.initialInfer, context, dependencies);
    const description = getPropertyDescription(property, resultType.name);

    return buildPreExpressionItem([], atom, description);
  });
}

function buildFetcher(context: ExpressionsContext, dependencies: ExpressionDependencies) {
  const fetchAtom = buildAtom(
    {transform: TransformerIdsEnum.FETCH},
    context.initialInfer,
    context,
    dependencies,
  );
  const parseJsonAtom = buildAtom(
    {transform: TransformerIdsEnum.TO_JSON, params: {type: 'object'}},
    fetchAtom.inferredType,
    context,
    dependencies,
  );
  const label = getMethodLabel(TransformerIdsEnum.FETCH);

  return buildPreExpressionItem([fetchAtom], parseJsonAtom, label);
}
